import type { DoseId } from '../../changes/ids';
import { domainTrace } from '../../diagnostics/domainTrace';
import type { PatientPersistenceRef } from '../../persistence/PersistenceRef';
import type { PersistenceRefReader } from '../../persistence/PersistenceRegistry';
import type { DatasetDeletionGateway } from '../../persistence/RegistrationPersistenceGateway';
import type { DatasetSaver, SaveScope } from '../save/DatasetSaver';
import { deleteDatasetRow, inScope, mapSaveErrors } from '../save/DatasetSaver';
import type { SaveEffect, SaveEntryResult } from '../save/SaveEffect';
import type { SealedSaveIntent } from '../save/SealedSaveIntent';

/**
 * Persists pending dose changes to the connector database. Driven by the
 * {@link PatientSaveService} orchestrator; entries are processed in
 * stable id order, each independently.
 *
 * Doses have no upload path yet, so the only entries classification
 * produces for them are deletions (an imported RTDOSE arms the
 * out-of-scope guard instead). When dose upload lands, it becomes this
 * saver's 'new' branch plus a sealed payload type — nothing outside this
 * file grows a dose case.
 */
export class DoseSaveService implements DatasetSaver {
    readonly entity = 'dose' as const;

    constructor(
        private readonly refs: PersistenceRefReader,
        private readonly datasetDeletion: DatasetDeletionGateway
    ) {}

    async saveEntries(
        sealed: SealedSaveIntent,
        _patientRef: PatientPersistenceRef,
        _overwrite: ReadonlySet<string> | null,
        scope: SaveScope = 'all'
    ): Promise<SaveEntryResult[]> {
        const ordered = [...sealed.intent.state.byDose.values()]
            .filter((entry) => inScope(entry.status, scope))
            .sort((a, b) => a.id.localeCompare(b.id));
        const results: SaveEntryResult[] = [];
        for (const entry of ordered) {
            const id = entry.id as DoseId;
            domainTrace.service('DoseSaveService.saveOne', {
                id,
                status: entry.status,
            });
            if (entry.status !== 'deleted') {
                // Classification never produces these today; if one ever
                // arrives, failing loudly beats mistaking it for a delete.
                results.push({
                    outcome: {
                        kind: 'failed',
                        entity: this.entity,
                        id,
                        message: 'Dose upload is not supported yet',
                    },
                    effects: [],
                });
                continue;
            }
            const effects: SaveEffect[] = [];
            const outcome = await mapSaveErrors(this.entity, id, () =>
                deleteDatasetRow(
                    this.refs,
                    this.entity,
                    id,
                    (ref) => this.datasetDeletion.deleteDose(ref),
                    effects
                )
            );
            results.push({ outcome, effects });
        }
        return results;
    }
}
